import { create } from 'zustand';
import { InsightsService } from '../lib/services/analytics/insightsService';
import { useAuthStore } from './authStore';
import { useOverviewStore } from './overviewStore';
import { insightsDateRangePresets } from '../lib/services/analytics/dateRangePresets';

const padTwo = (value: number) => String(value).padStart(2, '0');
const formatLocalDate = (date: Date): string =>
  `${date.getFullYear()}-${padTwo(date.getMonth() + 1)}-${padTwo(date.getDate())}`;

interface Question {
  id: string;
  question: string;
  views: number;
  successRate?: number;
  trend?: number;
  priority?: 'High' | 'Medium' | 'Low';
}

interface ChatRecord {
  sessionId: string;
  spanId: string;
  input: string;
  output: string;
  count: number;
  notMissedCount: number;
}

interface ClassificationGroup {
  classification: string;
  totalCount: number;
  totalNotMissedCount: number;
  successRate: number;
  records: ChatRecord[];
}

interface MetricCardData {
  title: string;
  value: string | number;
  subtitle?: string;
  change?: number;
  timeRange?: string;
  showViewReport?: boolean;
  chartData?: {
    labels: string[];
    data: number[];
  };
  chartColor?: string;
  yAxisLabel?: string;
  xAxisLabel?: string;
}

interface InsightsState {
  mostAskedQuestions: Question[];
  missedQuestions: Question[];
  classificationGroups: ClassificationGroup[];
  missedClassificationGroups: ClassificationGroup[];
  selectedClassification: ClassificationGroup | null;
  selectedMissedClassification: ClassificationGroup | null;
  isLoading: boolean;
  error: string | null;
  dateRange: { startTime: string; endTime: string };
  selectedPeriod: string;

  // Totals for ALL data (used for AI resolution rate)
  allDataTotalCount: number;
  allDataTotalResolved: number;

  // Unique resolved session count (deduplicated by sessionId, used for Time saved calculation)
  uniqueResolvedSessionCount: number;

  // Four metric cards data
  metricCards: MetricCardData[];
  cardLoadingStates: Record<string, boolean>;
  cardMetrics: Record<string, MetricCardData>;

  setMostAskedQuestions: (questions: Question[]) => void;
  setMissedQuestions: (questions: Question[]) => void;
  setClassificationGroups: (groups: ClassificationGroup[]) => void;
  setMissedClassificationGroups: (groups: ClassificationGroup[]) => void;
  setSelectedClassification: (classification: ClassificationGroup | null) => void;
  setSelectedMissedClassification: (classification: ClassificationGroup | null) => void;
  setLoading: (loading: boolean) => void;
  setDateRange: (range: { startTime: string; endTime: string }) => void;
  setSelectedPeriod: (period: string) => void;
  fetchQuestions: () => Promise<void>;
  fetchMetricCards: () => Promise<void>;
  fetchMetricForCard: (cardId: string, metricType: string, timeRange: string) => Promise<void>;
  reset: () => void;
}

export const useInsightsStore = create<InsightsState>((set, get) => ({
  mostAskedQuestions: [],
  missedQuestions: [],
  classificationGroups: [],
  missedClassificationGroups: [],
  selectedClassification: null,
  selectedMissedClassification: null,
  isLoading: false,
  error: null,
  dateRange: insightsDateRangePresets().last7Days,
  selectedPeriod: 'last7Days',

  // Totals for ALL data (used for AI resolution rate)
  allDataTotalCount: 0,
  allDataTotalResolved: 0,

  // Unique resolved session count (deduplicated by sessionId, used for Time saved calculation)
  uniqueResolvedSessionCount: 0,

  // Initialize metric cards data
  metricCards: [
    {
      title: 'AI involvement rate',
      value: '0%', // 0% if no conversations, 100% if there are conversations (Most Asked or Missed)
      subtitle: '',
      change: 0,
      chartColor: '#3B82F6',
    },
    {
      title: 'AI resolution rate',
      value: '0%',
      subtitle: '',
      change: 0,
      chartColor: '#10B981',
    },
    {
      title: 'Top questions success rate',
      value: '0%',
      subtitle: '',
      change: 0,
      chartColor: '#8B5CF6',
    },
    {
      title: 'Time saved',
      value: '-',
      subtitle: '',
      change: 0,
      chartColor: '#F59E0B',
    },
  ],
  cardLoadingStates: {},
  cardMetrics: {},

  setMostAskedQuestions: questions => set({ mostAskedQuestions: questions }),

  setMissedQuestions: questions => set({ missedQuestions: questions }),

  setClassificationGroups: groups => set({ classificationGroups: groups }),

  setMissedClassificationGroups: groups => set({ missedClassificationGroups: groups }),

  setSelectedClassification: classification => set({ selectedClassification: classification }),

  setSelectedMissedClassification: classification =>
    set({ selectedMissedClassification: classification }),

  setLoading: loading => set({ isLoading: loading }),

  setDateRange: range => {
    set({ dateRange: range });
    // Refetch both questions and metric cards when date range changes
    const state = get();
    state.fetchQuestions();
    // Refetch metric cards after questions are loaded
    setTimeout(() => {
      state.fetchMetricCards();
    }, 1000);
  },

  setSelectedPeriod: period => {
    set({ selectedPeriod: period });
    const presets = insightsDateRangePresets();
    let dateRange: { startTime: string; endTime: string } | null = null;

    if (period === 'last7Days') {
      dateRange = presets.last7Days;
    } else if (period === 'last30Days') {
      dateRange = presets.last30Days;
    } else if (period === 'last3Months') {
      dateRange = presets.last3Months;
    } else if (period === 'today') {
      const today = formatLocalDate(new Date());
      dateRange = { startTime: today, endTime: today };
    } else if (period === 'yesterday') {
      const yesterday = new Date();
      yesterday.setDate(yesterday.getDate() - 1);
      const yesterdayStr = formatLocalDate(yesterday);
      dateRange = { startTime: yesterdayStr, endTime: yesterdayStr };
    } else if (period === 'last12Months') {
      const today = new Date();
      const endTime = formatLocalDate(today);
      const startTime = formatLocalDate(new Date(today.getTime() - 365 * 24 * 60 * 60 * 1000));
      dateRange = { startTime, endTime };
    }

    if (dateRange) {
      set({ dateRange });
      const state = get();
      state.fetchQuestions();
      setTimeout(() => {
        state.fetchMetricCards();
      }, 1000);
    }
  },

  fetchQuestions: async () => {
    const state = get();

    // Prevent duplicate requests if already loading
    if (state.isLoading) {
      return;
    }

    const { user } = useAuthStore.getState();
    if (!user?.id) {
      set({ error: 'User not authenticated' });
      return;
    }

    set({ isLoading: true, error: null });

    try {
      const { dateRange } = get();

      // Fetch both most asked and missed questions in parallel
      const [mostAskedData, missedData] = await Promise.all([
        InsightsService.getMostAskedQuestions({
          startTime: dateRange.startTime,
          endTime: dateRange.endTime,
          userId: user.id,
        }),
        InsightsService.getMissedQuestions({
          startTime: dateRange.startTime,
          endTime: dateRange.endTime,
          userId: user.id,
        }),
      ]);

      // Group most asked questions by classification
      const classificationMap = new Map<string, ClassificationGroup>();

      mostAskedData.forEach(item => {
        const classification = item.classification;
        const count = parseInt(item.count, 10);
        const notMissedCount = parseInt(item.notMissedCount || '0', 10);

        if (!classificationMap.has(classification)) {
          classificationMap.set(classification, {
            classification,
            totalCount: 0,
            totalNotMissedCount: 0,
            successRate: 0,
            records: [],
          });
        }

        const group = classificationMap.get(classification)!;
        group.totalCount += count;
        group.totalNotMissedCount += notMissedCount;

        // Add individual chat record
        group.records.push({
          sessionId: item.sessionId || '',
          spanId: item.spanId || '',
          input: item.input || '',
          output: item.output || '',
          count,
          notMissedCount,
        });
      });

      // Calculate success rates and sort by total count
      const allClassificationGroups: ClassificationGroup[] = Array.from(classificationMap.values())
        .map(group => ({
          ...group,
          successRate:
            group.totalCount > 0
              ? Math.round((group.totalNotMissedCount / group.totalCount) * 100)
              : 0,
        }))
        .sort((a, b) => b.totalCount - a.totalCount);

      // Calculate totals for ALL data (used for AI resolution rate)
      const allDataTotalCount = allClassificationGroups.reduce(
        (sum, group) => sum + group.totalCount,
        0
      );
      const allDataTotalResolved = allClassificationGroups.reduce(
        (sum, group) => sum + group.totalNotMissedCount,
        0
      );

      // Calculate unique resolved session count by deduplicating sessionId
      // This is used for Time saved calculation (each unique session = one conversation)
      const resolvedSessionIds = new Set<string>();
      mostAskedData.forEach(item => {
        // Only count sessions where notMissedCount > 0 (resolved sessions)
        if (parseInt(item.notMissedCount || '0', 10) > 0 && item.sessionId) {
          resolvedSessionIds.add(item.sessionId);
        }
      });
      const uniqueResolvedSessionCount = resolvedSessionIds.size;

      // Only top 5 for display (used for Top questions success rate)
      const classificationGroups = allClassificationGroups.slice(0, 5);

      // Group missed questions by classification
      const missedClassificationMap = new Map<string, ClassificationGroup>();

      missedData.forEach(item => {
        const classification = item.classification;
        const missedCount = parseInt(item.missedCount || '0', 10);

        // Skip records that are not actually missed
        if (missedCount <= 0) {
          return;
        }

        if (!missedClassificationMap.has(classification)) {
          missedClassificationMap.set(classification, {
            classification,
            totalCount: 0,
            totalNotMissedCount: 0,
            successRate: 0,
            records: [],
          });
        }

        const group = missedClassificationMap.get(classification)!;
        group.totalCount += missedCount;

        // Add individual chat record
        group.records.push({
          sessionId: item.sessionId || '',
          spanId: item.spanId || '',
          input: item.input || '',
          output: item.output || '',
          count: missedCount,
          notMissedCount: 0,
        });
      });

      // Calculate success rates and sort by total count for missed questions
      const missedClassificationGroups: ClassificationGroup[] = Array.from(
        missedClassificationMap.values()
      )
        .map(group => ({
          ...group,
          successRate: 0,
        }))
        .sort((a, b) => b.totalCount - a.totalCount)
        .slice(0, 5); // Only top 5

      // Transform most asked questions to the expected format
      const transformedMostAsked: Question[] = mostAskedData.map((item, index) => ({
        id: `most-asked-${index}`,
        question: item.classification,
        views: parseInt(item.count, 10),
        successRate: item.notMissedCount
          ? Math.round((parseInt(item.notMissedCount, 10) / parseInt(item.count, 10)) * 100)
          : 0,
        trend: Math.floor(Math.random() * 20) - 10, // Mock trend data
      }));

      // Transform missed questions to the expected format
      const transformedMissed: Question[] = missedData
        .filter(item => parseInt(item.missedCount || '0', 10) > 0)
        .map((item, index) => {
          const missedCount = parseInt(item.missedCount || '0', 10);
          return {
            id: `missed-${index}`,
            question: item.classification,
            views: missedCount,
            priority:
              missedCount > 10
                ? 'High'
                : missedCount > 5
                  ? 'Medium'
                  : ('Low' as 'High' | 'Medium' | 'Low'),
          };
        });

      set({
        mostAskedQuestions: transformedMostAsked,
        missedQuestions: transformedMissed,
        classificationGroups,
        missedClassificationGroups,
        allDataTotalCount,
        allDataTotalResolved,
        uniqueResolvedSessionCount,
      });
    } catch (error) {
      console.error('Failed to fetch questions:', error);
      set({ error: error instanceof Error ? error.message : 'Failed to load insights data' });
    } finally {
      set({ isLoading: false });
    }
  },

  fetchMetricCards: async () => {
    const { user } = useAuthStore.getState();
    if (!user?.id) {
      return;
    }

    const { dateRange } = get();

    try {
      // Fetch metric data from APIs or calculate from existing data
      const {
        classificationGroups,
        missedClassificationGroups,
        allDataTotalCount,
        allDataTotalResolved,
        uniqueResolvedSessionCount,
      } = get();

      // Calculate metrics for Top 5 questions only (for Top questions success rate)
      const top5TotalCount = classificationGroups.reduce((sum, group) => sum + group.totalCount, 0);
      const top5TotalResolved = classificationGroups.reduce(
        (sum, group) => sum + group.totalNotMissedCount,
        0
      );

      // AI involvement rate: 100% if there are any conversations (Most Asked or Missed), 0% if empty
      const hasQuestions =
        (classificationGroups && classificationGroups.length > 0) ||
        (missedClassificationGroups && missedClassificationGroups.length > 0);
      const involvementRate = hasQuestions ? 100 : 0;

      // AI resolution rate: uses ALL data (not just top 5)
      const resolutionRate =
        allDataTotalCount > 0 ? Math.round((allDataTotalResolved / allDataTotalCount) * 100) : 0;

      // Top questions success rate: calculated from top 5 most asked questions only
      const topQuestionsSuccessRate =
        top5TotalCount > 0 ? Math.round((top5TotalResolved / top5TotalCount) * 100) : 0;

      // Get time saved from overviewStore (same data source as Overview page)
      // Ensure overview data matches current user + period
      const overviewState = useOverviewStore.getState();
      if (
        overviewState.currentUserId !== user.id ||
        overviewState.currentPeriod !== get().selectedPeriod ||
        !overviewState.timeSaved ||
        overviewState.timeSaved === '0 min'
      ) {
        await overviewState.fetchDashboardData(user.id, get().selectedPeriod);
      }
      const { timeSaved } = useOverviewStore.getState();

      const updatedCards = [
        {
          title: 'AI involvement rate',
          // 100% if there are conversations, 0% if empty
          value: `${involvementRate}%`,
          subtitle: '', // No subtitle numbers
          change: Math.floor(Math.random() * 20) - 10, // Mock change
          chartColor: '#3B82F6',
        },
        {
          title: 'AI resolution rate',
          value: `${resolutionRate}%`,
          subtitle: '', // No subtitle numbers
          change: Math.floor(Math.random() * 20) - 10, // Mock change
          chartColor: '#10B981',
        },
        {
          // Renamed from "Answer rate" to "Top questions success rate"
          // Formula uses only most asked questions: totalAskedResolved / totalAsked
          title: 'Top questions success rate',
          value: `${topQuestionsSuccessRate}%`,
          subtitle: '', // No subtitle numbers
          change: Math.floor(Math.random() * 20) - 10, // Mock change
          chartColor: '#8B5CF6',
        },
        {
          title: 'Time saved',
          value: timeSaved || '-',
          subtitle: '', // No subtitle numbers
          change: Math.floor(Math.random() * 20) - 10, // Mock change
          chartColor: '#F59E0B',
        },
      ];

      set({ metricCards: updatedCards });
    } catch (error) {
      console.error('Failed to fetch metric cards:', error);
    }
  },

  fetchMetricForCard: async (cardId: string, metricType: string, timeRange: string) => {
    set(state => ({
      cardLoadingStates: { ...state.cardLoadingStates, [cardId]: true },
    }));

    try {
      // Mock implementation - in real app, this would call specific APIs
      await new Promise(resolve => setTimeout(resolve, 1000));

      const mockMetric: MetricCardData = {
        title:
          get().metricCards.find((_, index) => `metric-${index}` === cardId)?.title || 'Metric',
        value: `${Math.floor(Math.random() * 100)}%`,
        subtitle: '', // No subtitle numbers
        change: Math.floor(Math.random() * 40) - 20,
        timeRange,
        chartData: {
          labels: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'],
          data: Array.from({ length: 7 }, () => Math.floor(Math.random() * 100)),
        },
      };

      set(state => ({
        cardMetrics: { ...state.cardMetrics, [cardId]: mockMetric },
        cardLoadingStates: { ...state.cardLoadingStates, [cardId]: false },
      }));
    } catch (error) {
      console.error(`Failed to fetch metric for card ${cardId}:`, error);
      set(state => ({
        cardLoadingStates: { ...state.cardLoadingStates, [cardId]: false },
      }));
    }
  },

  // Reset store state (for user switch)
  reset: () => {
    set({
      mostAskedQuestions: [],
      missedQuestions: [],
      classificationGroups: [],
      missedClassificationGroups: [],
      selectedClassification: null,
      selectedMissedClassification: null,
      isLoading: false,
      error: null,
      dateRange: insightsDateRangePresets().last7Days,
      selectedPeriod: 'last7Days',
      allDataTotalCount: 0,
      allDataTotalResolved: 0,
      uniqueResolvedSessionCount: 0,
      metricCards: [
        {
          title: 'AI involvement rate',
          value: '100%',
          subtitle: '',
          change: 0,
          chartColor: '#3B82F6',
        },
        {
          title: 'AI resolution rate',
          value: '0%',
          subtitle: '',
          change: 0,
          chartColor: '#10B981',
        },
        {
          title: 'Top questions success rate',
          value: '0%',
          subtitle: '',
          change: 0,
          chartColor: '#8B5CF6',
        },
        {
          title: 'Time saved',
          value: '-',
          subtitle: '',
          change: 0,
          chartColor: '#F59E0B',
        },
      ],
      cardLoadingStates: {},
      cardMetrics: {},
    });
  },
}));
